import React from "react";

/**
 * Generates a React component avatar with the given initial and background color
 * @param {string} initial - The initial to display (single character)
 * @param {string} backgroundColor - Background color (hex or CSS color)
 * @param {string} textColor - Text color (hex or CSS color)
 * @param {number} size - Size of the avatar in pixels (default: 130)
 * @returns {React.Component} - A React component that renders the avatar
 */
export function generateInitialAvatar(initial, backgroundColor = "#50bbe6", textColor = "#ffffff", size = 130) {
    return function InitialAvatar({ className = "", style = {}, ...props }) {
        return (
            <div
                className={`initial-avatar avatar ${className}`}
                style={{

                    backgroundColor,
                    fontSize: "13px",
                    height: size,
                    width: size,

                }}
                {...props}
            >
                {initial.toUpperCase()}
            </div>
        );
    };
}

/**
 * Gets a consistent background color for a workspace based on its name
 * Uses a simple hash function to ensure the same workspace always gets the same color
 * @param {string} workspaceName - The name of the workspace
 * @returns {string} - A hex color string
 */
export function getWorkspaceAvatarColor(workspaceName) {
    const colors = [
        "#00A0B0",
        "#97AA0F",
        "#2980B9",
        "#F39C12",
        "#BD1550",
        "#E74C3C"
    ];

    // Simple hash function to get consistent color for same workspace name
    let hash = 0;
    for (let i = 0; i < workspaceName.length; i++) {
        const char = workspaceName.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
}

/**
 * Generates an avatar React component for a team workspace using the first initial of the workspace name
 * This function combines the workspace name's first character with a consistent color
 * to create a visually appealing avatar that represents the team workspace.
 *
 * @param {string} workspaceName - The name of the workspace
 * @param {number} size - Size of the avatar in pixels (default: 130)
 * @returns {React.Component} - A React component that renders the workspace avatar
 */
export function generateTeamWorkspaceAvatar(workspaceName, size = 130) {
    const initial = workspaceName.charAt(0);
    const backgroundColor = getWorkspaceAvatarColor(workspaceName);

    return generateInitialAvatar(initial, backgroundColor, "#ffffff", size);
}
